import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';

// ---------------------------------------------------------------------------
// CircularProgress – SVG ring progress indicator
// ---------------------------------------------------------------------------

/**
 * Color theme options for the progress ring
 */
export type CircularProgressColor =
  | 'emerald'
  | 'blue'
  | 'violet'
  | 'amber'
  | 'teal'
  | 'red'
  | 'green'
  | 'indigo'
  | 'gray';

/**
 * Size presets for the circular progress
 */
export type CircularProgressSize = 'sm' | 'md' | 'lg' | 'xl';

interface CircularProgressSignature {
  Args: {
    /** Current value (0 to max) */
    value: number;
    /** Maximum value (default: 100) */
    max?: number;
    /** Color theme for the progress ring */
    color?: CircularProgressColor;
    /** Size preset (default: 'md') */
    size?: CircularProgressSize;
    /** Stroke width (default: auto based on size) */
    strokeWidth?: number;
    /** Whether to animate the ring on mount */
    animated?: boolean;
    /** Custom aria-label */
    label?: string;
  };
  Blocks: {
    /** Content rendered inside the ring (e.g., value text, icon) */
    default?: [];
  };
  Element: HTMLDivElement;
}

/**
 * Map the open `@color` API to a semantic intent class
 * (`.tio-progress-ring--*` in `addons/tio-ui/src/styles/`).
 * Each intent class sets `stroke` to a mode-aware token; dark mode
 * cascades automatically.
 *
 * Off-palette decoratives (violet / indigo) re-home to BRAND per the
 * theme-migration playbook ("Off-palette → brand, not info/teal").
 */
const colorStrokeClasses: Record<CircularProgressColor, string> = {
  emerald: 'tio-progress-ring--success',
  blue: 'tio-progress-ring--info',
  violet: 'tio-progress-ring--brand',
  amber: 'tio-progress-ring--warning',
  teal: 'tio-progress-ring--action',
  red: 'tio-progress-ring--danger',
  green: 'tio-progress-ring--success',
  indigo: 'tio-progress-ring--brand',
  gray: 'tio-progress-ring--neutral',
};

const sizeDimensions: Record<CircularProgressSize, { size: number; stroke: number; text: string }> =
  {
    sm: { size: 48, stroke: 4, text: 'text-sm' },
    md: { size: 80, stroke: 6, text: 'text-lg' },
    lg: { size: 120, stroke: 8, text: 'text-2xl' },
    xl: { size: 160, stroke: 10, text: 'text-3xl' },
  };

/**
 * CircularProgress - An SVG ring progress indicator
 *
 * Displays a circular ring that fills based on the value/max ratio.
 * Supports named block content inside the ring for value display.
 *
 * @example Basic usage
 * ```gts
 * import CircularProgress from 'tio-ui/components/circular-progress';
 *
 * <CircularProgress @value={{75}} @color="emerald">
 *   75%
 * </CircularProgress>
 * ```
 *
 * @example Large with custom content
 * ```gts
 * <CircularProgress @value={{720}} @max={{1000}} @color="violet" @size="lg">
 *   <div class="text-center">
 *     <div class="tio-text-2xl-bold">720</div>
 *     <div class="text-xs tio-fg-muted">of 1,000</div>
 *   </div>
 * </CircularProgress>
 * ```
 */
export default class CircularProgress extends Component<CircularProgressSignature> {
  get max() {
    const max = this.args.max ?? 100;
    return max > 0 ? max : 100;
  }

  get percent() {
    const value = Math.max(0, Math.min(this.args.value, this.max));
    return Math.round((value / this.max) * 100);
  }

  get sizeKey() {
    return this.args.size ?? 'md';
  }

  get dimensions() {
    return sizeDimensions[this.sizeKey];
  }

  get svgSize() {
    return this.dimensions.size;
  }

  get center() {
    return this.svgSize / 2;
  }

  get strokeWidth() {
    return this.args.strokeWidth ?? this.dimensions.stroke;
  }

  get radius() {
    return (this.svgSize - this.strokeWidth) / 2;
  }

  get circumference() {
    return 2 * Math.PI * this.radius;
  }

  get dashOffset() {
    return this.circumference * (1 - this.percent / 100);
  }

  get containerStyle() {
    return htmlSafe(`width: ${this.svgSize}px; height: ${this.svgSize}px`);
  }

  get strokeClass() {
    return colorStrokeClasses[this.args.color ?? 'blue'];
  }

  get ariaLabel() {
    return this.args.label ?? `${this.percent}% complete`;
  }

  <template>
    <div
      data-test-circular-progress
      class="tio-inline-row-center relative"
      style={{this.containerStyle}}
      role="progressbar"
      aria-valuenow={{this.percent}}
      aria-valuemin={{0}}
      aria-valuemax={{100}}
      aria-label={{this.ariaLabel}}
      ...attributes
    >
      <svg
        role="presentation"
        class="{{if @animated 'transition-all duration-700'}} -rotate-90"
        width={{this.svgSize}}
        height={{this.svgSize}}
      >
        {{! Background track }}
        <circle
          role="presentation"
          class="tio-progress-ring__track"
          fill="none"
          stroke-width={{this.strokeWidth}}
          cx={{this.center}}
          cy={{this.center}}
          r={{this.radius}}
        />
        {{! Progress ring }}
        <circle
          role="presentation"
          data-test-circular-progress-ring
          class="{{this.strokeClass}} {{if @animated 'transition-all duration-700'}}"
          fill="none"
          stroke-width={{this.strokeWidth}}
          stroke-linecap="round"
          cx={{this.center}}
          cy={{this.center}}
          r={{this.radius}}
          stroke-dasharray={{this.circumference}}
          stroke-dashoffset={{this.dashOffset}}
        />
      </svg>
      {{#if (has-block)}}
        <div data-test-circular-progress-content class="tio-row-center absolute inset-0">
          {{yield}}
        </div>
      {{/if}}
    </div>
  </template>
}

export type { CircularProgressSignature };
